import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, Plus, Trash2 } from "lucide-react";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import TimePicker from "@/components/TimePicker";
import PasswordRequirements, { isPasswordStrong } from "@/components/PasswordRequirements";
import { COUNTRIES, DEFAULT_COUNTRY } from "@/lib/countries";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PENDING_KEY = "wv:pending-signup";

let LOC_SEQ = 0;
const emptyLoc = () => ({
  uid: `loc-${++LOC_SEQ}`,
  label: null,
  lat: null,
  lon: null,
  country: DEFAULT_COUNTRY,
  deliveryTime: "07:00",
});

/** Combined signup + first-subscription form. The landing page IS this form.
 *  Email/password submit hits /api/auth/signup-with-locations.
 *  Google/Apple buttons stash the form state in localStorage and redirect to
 *  the OAuth provider — AppDashboard reads the stash on landing and creates
 *  the subscriptions via /api/manage/{token}/subscriptions. */
export default function SignupSubscribeForm({ onPreview, previewing }) {
  const navigate = useNavigate();
  const { refreshSelf } = useAuth();
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [locations, setLocations] = useState([emptyLoc()]);
  const [wantsStocks, setWantsStocks] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const updateLoc = (i, patch) =>
    setLocations((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLoc = () => setLocations((ls) => [...ls, emptyLoc()]);
  const removeLoc = (i) => setLocations((ls) => ls.filter((_, idx) => idx !== i));

  const baseValid =
    firstName.trim() &&
    email.trim() &&
    locations.every((l) => l.lat && l.lon && l.deliveryTime);
  const canSubmit = baseValid && isPasswordStrong(password);
  // OAuth doesn't need a fully-resolved location — Google/Apple just need
  // an account. Users complete the location step on the dashboard after
  // sign-in (the stash is best-effort: any locations with lat/lon DO get
  // applied, the rest are dropped and the user re-enters them).
  const oauthValid = !!(firstName.trim() && email.trim());

  const buildLocationsPayload = () =>
    locations
      // Only include locations the user has actually resolved via the
      // autocomplete dropdown — typing a partial address without picking
      // a suggestion would otherwise stash a junk row that fails server
      // validation and silently drops the whole stash.
      .filter((l) => l.lat && l.lon && l.deliveryTime)
      .map((l) => ({
        label: l.label,
        lat: l.lat,
        lon: l.lon,
        country: l.country || DEFAULT_COUNTRY,
        delivery_time: l.deliveryTime,
      }));

  const stashPendingForOAuth = () => {
    if (!oauthValid) return false;
    try {
      localStorage.setItem(
        PENDING_KEY,
        JSON.stringify({
          first_name: firstName.trim(),
          email: email.trim(),
          locations: buildLocationsPayload(),
          // 30-min TTL — long enough for OAuth round trip, short enough that
          // stale stashes don't haunt later signups on the same device.
          expires_at: Date.now() + 30 * 60 * 1000,
        }),
      );
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!baseValid) {
      toast.error("Fill in your name, email, and validate every address first.");
      return;
    }
    if (!isPasswordStrong(password)) {
      setError("Make your password stronger — see the checklist below.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/auth/signup-with-locations`,
        {
          first_name: firstName.trim(),
          email: email.trim().toLowerCase(),
          password,
          locations: buildLocationsPayload(),
          include_stocks: wantsStocks,
        },
        { withCredentials: true },
      );
      // Refresh AuthContext so RequireAuth lets us into /app immediately.
      await refreshSelf();
      navigate("/app", { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't create your account.";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOAuth = (provider) => {
    if (!oauthValid) {
      toast.error("Add your name and email above first.");
      return;
    }
    if (!stashPendingForOAuth()) {
      toast.error("Couldn't save your progress. Try again or use email/password below.");
      return;
    }
    if (provider === "google") {
      window.location.href = `${API}/auth/google/start?next=${encodeURIComponent("/app")}`;
      return;
    }
    if (provider === "apple") {
      window.location.href = `${API}/auth/apple/start?next=${encodeURIComponent("/app")}`;
      return;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-12 space-y-6 max-w-xl" data-testid="signup-subscribe-form">
      <div>
        <label className="ticker-label block mb-2">First name</label>
        <input
          data-testid="first-name-input"
          className="w-full px-4 py-3 bg-white border border-black outline-none text-[15px] focus:bg-[#FFFBE6]"
          placeholder="e.g. Avery"
          value={firstName}
          autoComplete="given-name"
          onChange={(e) => setFirstName(e.target.value)}
        />
      </div>

      <div>
        <label className="ticker-label block mb-2">Email</label>
        <input
          data-testid="email-input"
          type="email"
          className="w-full px-4 py-3 bg-white border border-black outline-none text-[15px] focus:bg-[#FFFBE6]"
          placeholder="you@domain.com"
          value={email}
          autoComplete="email"
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>

      {locations.map((loc, i) => (
        <div key={loc.uid} className="border border-black p-4 space-y-4 bg-white" data-testid={`location-row-${i}`}>
          <div className="flex items-center justify-between">
            <div className="ticker-label">Location {i + 1}</div>
            {locations.length > 1 && (
              <button
                type="button"
                onClick={() => removeLoc(i)}
                data-testid={`remove-location-${i}`}
                className="text-xs uppercase tracking-wider hover:text-red-600 flex items-center gap-1"
              >
                <Trash2 className="w-3 h-3" /> Remove
              </button>
            )}
          </div>
          <div>
            <div className="flex items-center justify-between mb-2 gap-2">
              <label className="ticker-label">
                Street address or ZIP{" "}
                <span className="normal-case tracking-normal text-neutral-500 text-[10px]">
                  (address recommended for greater accuracy)
                </span>
              </label>
              <select
                data-testid={`country-select-${i}`}
                value={loc.country || DEFAULT_COUNTRY}
                onChange={(e) => updateLoc(i, { country: e.target.value, label: null, lat: null, lon: null })}
                className="text-[11px] tracking-wider uppercase border border-black bg-white px-2 py-1 outline-none focus:bg-[#FFFBE6]"
                aria-label="Country"
              >
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </select>
            </div>
            <AddressAutocomplete
              testId={`address-input-${i}`}
              country={loc.country || DEFAULT_COUNTRY}
              onSelect={(addr) => updateLoc(i, { lat: addr.lat, lon: addr.lon, label: addr.label })}
            />
          </div>
          <div>
            <label className="ticker-label block mb-2">Send me the forecast at</label>
            <TimePicker value={loc.deliveryTime} onChange={(v) => updateLoc(i, { deliveryTime: v })} />
            <div className="mt-2 ticker-label">Local time at this address</div>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={addLoc}
        data-testid="add-location-button"
        className="group w-full border border-black bg-white py-3 text-sm uppercase tracking-[0.14em] font-semibold flex items-center justify-center gap-3 hover:bg-[#FFD600] transition-colors"
      >
        <span className="inline-flex w-5 h-5 items-center justify-center bg-black text-[#FFD600] group-hover:bg-[#0A0A0A]">
          <Plus className="w-3 h-3" strokeWidth={3} />
        </span>
        Add another location
      </button>

      {/* Top-10 Stocks add-on — one-click subscribe. Prepends to your
          daily news email, or arrives as a 07:00-local standalone if
          you have no news feed. Not investment advice — see disclaimer
          on the Top 10 tab post-signup. */}
      <label
        htmlFor="signup-stocks-optin"
        data-testid="signup-stocks-optin-label"
        className={`block cursor-pointer border-2 ${
          wantsStocks ? "border-black bg-[#FFFBE6]" : "border-black/25 bg-white hover:border-black/60"
        } p-4 transition-colors`}
      >
        <div className="flex items-start gap-3">
          <input
            id="signup-stocks-optin"
            type="checkbox"
            data-testid="signup-stocks-optin"
            checked={wantsStocks}
            onChange={(e) => setWantsStocks(e.target.checked)}
            className="mt-1 w-4 h-4 accent-black"
          />
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="ticker-label !text-[10px] !text-black">
                Also send me the Top 10 Stocks
              </div>
              <span className="inline-block px-1.5 py-0.5 bg-black text-[#FFD600] text-[9px] uppercase tracking-[0.16em] font-bold">
                Free
              </span>
            </div>
            <p className="text-[13px] text-neutral-700 leading-snug mt-1.5">
              A daily consensus watch across Goldman, Morgan Stanley, JPMorgan, BofA and 8 other major research desks — with adds &amp; removals since yesterday. Ships at the top of your email. Not investment advice.
            </p>
          </div>
        </div>
      </label>

      <div className="pt-2">
        <div className="ticker-label mb-3">Create your account with</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => handleOAuth("google")}
            disabled={!oauthValid}
            data-testid="signup-oauth-google"
            className="inline-flex items-center justify-center gap-3 px-4 py-3 border-2 border-black bg-white hover:bg-[#FFFBE6] text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
            title={oauthValid ? "Continue with Google" : "Add your name and email first"}
          >
            Continue with Google
          </button>
          <button
            type="button"
            onClick={() => handleOAuth("apple")}
            disabled={!oauthValid}
            data-testid="signup-oauth-apple"
            title={oauthValid ? "Continue with Apple" : "Add your name and email first"}
            className="inline-flex items-center justify-center gap-3 px-4 py-3 border-2 border-black bg-black text-white hover:bg-neutral-800 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Continue with Apple
          </button>
        </div>
        <p className="mt-2 text-[11px] text-neutral-500 leading-relaxed">
          We bring your name, email, and locations along — no re-typing.
        </p>
      </div>

      <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.18em] text-neutral-400">
        <div className="flex-1 h-px bg-neutral-300" /> or create a password
        <div className="flex-1 h-px bg-neutral-300" />
      </div>

      <div>
        <input
          data-testid="password-input"
          type="password"
          className="w-full px-4 py-3 bg-white border border-black outline-none text-[15px] focus:bg-[#FFFBE6]"
          placeholder="Min 10 chars, mix of cases / digits / symbols"
          value={password}
          autoComplete="new-password"
          onChange={(e) => setPassword(e.target.value)}
        />
        <div className="mt-3">
          <PasswordRequirements value={password} />
        </div>
      </div>

      {error && (
        <div data-testid="signup-error" className="text-sm text-red-600 -mb-2">
          {error}
        </div>
      )}

      <div className="flex items-center gap-4 pt-2 flex-wrap">
        <button
          type="submit"
          disabled={submitting || !canSubmit}
          data-testid="signup-submit-button"
          className="btn-brutal inline-flex items-center gap-3 px-7 py-4 text-sm tracking-[0.14em] uppercase font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
          {submitting ? "Setting things up" : "Let’s go"}
        </button>
        <button
          type="button"
          onClick={() => onPreview?.(locations)}
          disabled={previewing || !locations.some((l) => l.lat)}
          data-testid="preview-forecast-button"
          className="inline-flex items-center gap-3 px-5 py-4 text-sm tracking-[0.14em] uppercase font-semibold border border-black bg-white hover:bg-[#FFD600] transition-colors disabled:opacity-40"
        >
          {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Preview forecast
        </button>
      </div>
    </form>
  );
}
